import React, { useState, useEffect, useCallback } from 'react';
import api, { uploadMedia } from '../utils/api';
import ImageCropperModal from './ImageCropperModal';
import './AddServiceStaffDialog.css';

const AddServiceStaffDialog = ({ onClose, onAdd, subunitName, serviceId, subunitId }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    email: '',
    designation: '',
    phone: '',
    service_ref: serviceId || '',
    dept_ref: subunitId || '',
    dp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [availableSubunits, setAvailableSubunits] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      if (response.data.success) {
        setServices(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  const fetchSubunitsForService = useCallback(async (serviceId) => {
    try {
      if (services.length === 0) return; // Wait for services to load
      
      const service = services.find(s => s._id === serviceId);
      if (service) {
        const response = await api.get(`/service-subunits?administration_ref=${service._id}`);
        if (response.data.success) {
          setAvailableSubunits(response.data.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch subunits for service:', err);
    }
  }, [services]);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      service_ref: serviceId || prev.service_ref,
      dept_ref: subunitId || prev.dept_ref
    }));
  }, [serviceId, subunitId]);

  useEffect(() => {
    if (formData.service_ref) {
      fetchSubunitsForService(formData.service_ref);
    }
  }, [formData.service_ref, services, fetchSubunitsForService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      // Open cropper modal
      setShowCropper(true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setShowCropper(false);
    
    // Create a File object from the blob
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        dp: e.target.result
      }));
    };
    reader.readAsDataURL(croppedBlob);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const result = await uploadMedia(selectedFile);
      if (result.success) {
        return result.url;
      } else {
        setError('Upload failed: ' + (result.error || 'Unknown error'));
        return null;
      }
    } catch (error) {
      setError('Upload failed: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDP = () => {
    setFormData(prev => ({
      ...prev,
      dp: ''
    }));
    setSelectedFile(null);
    const fileInput = document.getElementById('dp-upload-service-staff');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    if (!serviceId && !formData.service_ref) {
      setError('Service is required');
      return;
    }

    if (!formData.dept_ref) {
      setError('Subunit is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload DP if selected
      let dpUrl = '';
      if (selectedFile) {
        dpUrl = await uploadSelectedFile();
        if (dpUrl === null) {
          setLoading(false);
          return; // Upload failed, error already set
        }
      }

      const dataWithDefaults = {
        ...formData,
        dp: dpUrl,
        priority: 1, // Default priority
        departmentId: 1 // Default department ID
      };

      await onAdd(dataWithDefaults);
      
      // Reset form and close dialog on success
      setFormData({
        name: '',
        nameEn: '',
        email: '',
        designation: '',
        phone: '',
        service_ref: serviceId || '',
        dept_ref: subunitId || '',
        dp: ''
      });
      setSelectedFile(null);
      setUploading(false);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Add New Staff Member to {subunitName}</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              placeholder="Enter designation"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="service_ref">Service Unit *</label>
            <select
              id="service_ref"
              name="service_ref"
              value={formData.service_ref}
              onChange={handleInputChange}
              required
              disabled={loading || Boolean(serviceId)}
              className="form-input"
            >
              <option value="">Select a service unit</option>
              {services.map((service) => (
                <option key={service._id} value={service._id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dept_ref">Subunit *</label>
            <select
              id="dept_ref"
              name="dept_ref"
              value={formData.dept_ref}
              onChange={handleInputChange}
              required
              disabled={loading || Boolean(subunitId) || !formData.service_ref}
              className="form-input"
            >
              <option value="">Select a subunit</option>
              {availableSubunits.map((subunit) => (
                <option key={subunit._id} value={subunit._id}>
                  {subunit.name} ({subunit.nameEn})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dp-upload-service-staff">Profile Picture</label>
            <div className="avatar-upload-container">
              <div 
                className={`circular-avatar ${selectedFile || formData.dp ? 'has-content' : ''}`}
                onClick={() => !loading && !uploading && document.getElementById('dp-upload-service-staff').click()}
              >
                {formData.dp ? (
                  <img src={formData.dp} alt="Profile" className="avatar-image" loading="lazy" decoding="async" />
                ) : selectedFile ? (
                  <img 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Profile" 
                    className="avatar-image preview" 
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="avatar-icon">📷</span>
                    <span className="avatar-text">Click to upload</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="dp-upload-service-staff"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={loading || uploading}
                className="file-input-hidden"
                style={{ display: 'none' }}
              />
              
              {(selectedFile || formData.dp) && (
                <div className="avatar-actions">
                  {selectedFile && !formData.dp && (
                    <button 
                      type="button"
                      onClick={uploadSelectedFile}
                      disabled={loading || uploading}
                      className="upload-button"
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                  {formData.dp && (
                    <button 
                      type="button" 
                      onClick={handleRemoveDP}
                      className="remove-button"
                      disabled={loading || uploading}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={
                loading ||
                !formData.name.trim() ||
                !formData.email.trim() ||
                !formData.designation.trim() ||
                !formData.service_ref ||
                !formData.dept_ref
              }
            >
              {loading ? 'Adding...' : 'Add Staff'}
            </button>
          </div>
        </form>

        {showCropper && selectedFile && (
          <ImageCropperModal
            imageFile={selectedFile}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              setShowCropper(false);
              setSelectedFile(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AddServiceStaffDialog;
